import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { registerUser, resendVerificationEmail } from "../api";
import Headline from "../Components/Elements/Headline";
import BtnPrimaryM from "../Components/Buttons/BtnPrimaryM";
import InputText from "../Components/Inputs/InputText";
import InputCheckbox from "../Components/Inputs/InputCheckbox";
import { useLanguage } from "../context/LanguageContext";
import Subheadline from "../Components/Elements/Subheadline";
import ReCAPTCHA from "react-google-recaptcha";
import Toast from "../Components/Elements/Toast";
import AuthWrapper from "../Components/Native/AuthWrapper";
import { trackRegistration, trackPageView } from "../utils/analytics";

const Register = () => {
  const { copy } = useLanguage();
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  const [captchaToken, setCaptchaToken] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [toast, setToast] = useState({ message: "", type: "success" });

  // Validation schema for registration (moved inside component)
  const registerSchema = z
    .object({
      email: z
        .string()
        .email(copy.register.errors.invalid_email)
        .min(1, copy.register.errors.invalid_email),
      password: z.string().min(12, copy.register.errors.password_min_length),
      confirmPassword: z
        .string()
        .min(1, copy.register.errors.confirm_password_required),
      termsAccepted: z.boolean().refine((val) => val === true, {
        message: copy.register.errors.terms_required,
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: copy.register.errors.passwords_do_not_match,
      path: ["confirmPassword"],
    });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
    mode: "onSubmit",
  });

  useEffect(() => {
    // Track page view
    trackPageView("/register");

    const params = new URLSearchParams(location.search);
    if (params.get("delete") === "true") {
      setToast({
        message: copy.profile.errors.account_deleted,
        type: "success",
      });
    }
  }, []);

  const onSubmit = async (data) => {
    if (!captchaToken) {
      setToast({
        message: copy.register.errors.captcha_required,
        type: "error",
      });
      return;
    }

    try {
      const response = await registerUser({ ...data, captchaToken });
      console.log("Registration successful:", response);

      // Track successful registration
      trackRegistration("email");

      // Move to step 2 in both cases - new registration and re-registration
      setCurrentStep(2);

      // Show appropriate message
      setToast({
        message: response.message,
        type: "success",
      });
    } catch (err) {
      console.error("Registration failed:", err.message);
      setToast({
        message: err.message || copy.register.errors.registration_failed,
        type: "error",
      });
    }
  };

  const handleResendVerificationEmail = async () => {
    try {
      const email = watch("email");
      const response = await resendVerificationEmail(email);
      console.log("Verification email sent again:", response);
      setToast({
        message: copy.register.errors.verification_email_resent,
        type: "success",
      });
    } catch (err) {
      console.error("Error resending verification email:", err.message);
      setToast({
        message: `${err.message || copy.register.errors.unable_to_resend}`,
        type: "error",
      });
    }
  };

  const getStepClass = (step) =>
    currentStep === step
      ? "w-8 h-8 flex items-center justify-center bg-primary rounded-full card-base-border"
      : "w-8 h-8 flex items-center justify-center bg-white text-blue-800 rounded-full card-base-border";

  return (
    <>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}
      <div className="w-full flex flex-col justify-center items-center background-page-color gap-8 h-full px-4 py-10 sm:px-8 sm:py-16 lg:px-20 lg:py-20 ">
        <a
          href="https://www.niumi.dk/"
          target="_blank"
          rel="noopener noreferrer"
          className="transition-all duration-300 font-accent color-primary text-5xl"
        >
          NIUMI
        </a>
        <div className="flex flex-col gap-6 items-center">
          <Headline
            headline={copy.register.headline}
            description={copy.register.description}
            center
            className="!block"
          />
          <div className="flex gap-5 items-center justify-center">
            <div className="flex gap-1 items-center">
              <div className={getStepClass(1)}>
                <p
                  className={`text-sm font-medium ${currentStep === 1 ? "!text-white" : ""}`}
                >
                  1
                </p>
              </div>
              <p>{copy.register.steps.personal_data}</p>
            </div>
            <div className="flex gap-1 items-center">
              <div className={getStepClass(2)}>
                <p
                  className={`text-sm font-medium ${currentStep === 2 ? "!text-white" : ""}`}
                >
                  2
                </p>
              </div>
              <p>{copy.register.steps.email_confirmation}</p>
            </div>
          </div>
        </div>

        <div className="card-base p-6 sm:p-8 card-base-border bg-white w-full max-w-[90vw] sm:w-[35vw] gap-4 sm:gap-6 flex flex-col">
          {currentStep === 1 && (
            <>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-3"
              >
                <InputText
                  placeholder={copy.register.form.placeholder_email}
                  label={copy.register.form.label_email}
                  {...register("email")}
                  error={errors.email?.message}
                  details={copy.register.form.details_email}
                />
                <InputText
                  label={copy.register.form.label_password}
                  type="password"
                  {...register("password")}
                  error={errors.password?.message}
                  details={copy.register.form.details_password}
                />
                <InputText
                  label={copy.register.form.label_confirm_password}
                  type="password"
                  {...register("confirmPassword")}
                  error={errors.confirmPassword?.message}
                />
                <div className="flex items-start gap-2 mt-2">
                  <InputCheckbox
                    id="termsAccepted"
                    label={
                      <>
                        {copy.register.form.terms_text_prefix}{" "}
                        <a
                          href="https://www.niumi.dk/legal/terms-of-service/"
                          className="color-primary hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {copy.register.form.terms_link}
                        </a>{" "}
                        {copy.register.form.terms_text_and}{" "}
                        <a
                          href="https://www.niumi.dk/legal/privacy-policy/"
                          className="color-primary hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {copy.register.form.privacy_link}
                        </a>
                      </>
                    }
                    {...register("termsAccepted")}
                    defaultChecked={true}
                    error={errors.termsAccepted?.message}
                  />
                </div>
                <ReCAPTCHA sitekey={siteKey} onChange={setCaptchaToken} />
                <div className="flex self-center">
                  <BtnPrimaryM
                    buttonCTA={copy.register.form.button_submit}
                    type="submit"
                  />
                </div>
              </form>
              <div className="flex items-center gap-4 my-2">
                <div className="flex-1 h-px card-base-border-top"></div>
                <p className="text-sm">{copy.register.form.text_or}</p>
                <div className="flex-1 h-px card-base-border-top"></div>
              </div>
              <AuthWrapper />
            </>
          )}

          {currentStep === 2 && (
            <>
              <Subheadline
                subheadline={copy.register.form.subheadline_confirm_email}
                description={copy.register.form.description_confirm_email}
              />
              <div className="flex self-center">
                <BtnPrimaryM
                  buttonCTA={copy.register.form.button_resend}
                  onClickLink={handleResendVerificationEmail}
                />
              </div>
            </>
          )}
          <div className="text-center flex gap-1 text-sm self-center">
            <p className="">{copy.register.form.text_already_registered}</p>
            <Link
              to="/login"
              className="text-primary hover:text-primary-dark hover:underline cursor-pointer"
            >
              {copy.register.form.text_login}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
